import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  IndexTable,
  Spinner,
  Banner,
  Button,
  Modal,
  useBreakpoints,
} from '@shopify/polaris';
import { formatCurrency } from '../utils/memoizedFormatters';
import apiClient from '../utils/api';

const LB_TO_KG = 0.45359237;

/**
 * SEED weight bands.
 *
 * These are NOT presented as the brand's data - they are the editable canvas. The pricing
 * grid is the only place an admin can create a ShippingFeeTable row, and there is no
 * "add a weight band" control and no endpoint that invents one, so deriving the bands
 * purely from existing rows would render an empty, unusable grid for any brand whose fees
 * have not been entered yet (which is every brand before the seed). The real bands found in
 * the database are UNIONED on top of this seed (see buildWeightBands), so a saved row on a
 * band outside this ladder is visible and editable rather than silently dropped.
 *
 * Kept in sync with the same ladder in shipping.controller.js:526-551, which is what
 * GET /brands/:brandId/shipping-pricing keys its response on.
 */
const SEED_WEIGHT_BANDS = [
  // 0-10 lb: 1-lb intervals
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5],
  [5, 6], [6, 7], [7, 8], [8, 9], [9, 10],
  // 10-20 lb: 2-lb intervals
  [10, 12], [12, 14], [14, 16], [16, 18], [18, 20],
  // 20-50 lb: 5-lb intervals
  [20, 25], [25, 30], [30, 35], [35, 40], [40, 45], [45, 50],
];

/**
 * LAST-RESORT service column list.
 *
 * Only used when neither GET /brands/:brandId/shipping-pricing nor
 * GET /brands/:brandId/shipping-rules yields a single shipping method, i.e. we have no
 * database-backed answer at all. When this fires the UI says so in a banner - it never
 * pretends these three are configured data.
 */
const FALLBACK_SERVICE_NAMES = ['BoxC Parcel', 'BoxC Plus', 'BoxC Priority'];

const bandIdFor = (min, max) => `${min}-${max}`;

// Renders 0 -> "0", 1 -> "0.45" so DB-derived bands read identically to the seeded ones.
const kgValue = (lb) => (lb * LB_TO_KG).toFixed(2).replace(/\.00$/, '');

const makeBand = (min, max) => ({
  id: bandIdFor(min, max),
  range: `${min}-${max} lb`,
  kg: `(${kgValue(min)}-${kgValue(max)} kg)`,
  min,
  max,
});

/**
 * Union the seed ladder with every band actually present on a ShippingFeeTable row,
 * sorted by weight so an out-of-ladder band lands in the right place in the table.
 */
const buildWeightBands = (ruleRows) => {
  const byId = new Map();
  SEED_WEIGHT_BANDS.forEach(([min, max]) => {
    const band = makeBand(min, max);
    byId.set(band.id, band);
  });

  ruleRows.forEach((row) => {
    const min = Number(row?.weightMin);
    const max = Number(row?.weightMax);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    const band = makeBand(min, max);
    if (!byId.has(band.id)) byId.set(band.id, band);
  });

  return Array.from(byId.values()).sort((a, b) => a.min - b.min || a.max - b.max);
};

/**
 * Weight-based shipping pricing table.
 *
 * Service columns and weight bands are both derived from the API (see loadPricingData).
 * ShippingFeeTable.shippingMethod is what billing matches against Order.boxcService
 * (shipping-calculation-engine.js:35-36), so the column set is the set of shipping methods
 * the database actually knows about - not a list this component asserts.
 */
const ShippingPricingTable = ({
  brand,
  onSave,
  loading = false,
  disabled = false
}) => {
  const { smUp } = useBreakpoints();

  // Service columns, derived from the API. Shape: [{ id, name }] where id === the raw
  // ShippingFeeTable.shippingMethod value.
  const [services, setServices] = useState([]);
  const [usingFallbackServices, setUsingFallbackServices] = useState(false);

  // Weight bands, seed ladder unioned with the bands found in the database.
  const [weightRanges, setWeightRanges] = useState(() => buildWeightBands([]));

  // How many DB rows the fixed /shipping-pricing grid dropped and we recovered from
  // /shipping-rules. Surfaced in the UI so the recovery is not silent.
  const [recoveredRowCount, setRecoveredRowCount] = useState(0);
  const [rulesUnavailable, setRulesUnavailable] = useState(false);

  // Pricing data state - structure: { serviceId: { weightRangeId: price } }
  const [pricingData, setPricingData] = useState({});
  const [originalPricingData, setOriginalPricingData] = useState({}); // Track original data for diff
  const [editingCell, setEditingCell] = useState(null); // { serviceId, weightRangeId }
  const [tempValues, setTempValues] = useState({});
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [overrideKeys, setOverrideKeys] = useState(new Set()); // Track which cells are brand overrides
  const [isDefaultMode, setIsDefaultMode] = useState(false); // Whether editing global defaults
  const [showResetModal, setShowResetModal] = useState(false); // Reset confirmation modal
  const [resetting, setResetting] = useState(false); // Loading state for reset

  // Load existing pricing data from API
  //
  // Two real sources, both read from ShippingFeeTable:
  //
  // 1. GET /brands/:brandId/shipping-pricing (shipping.controller.js:448)
  //      -> { success, brandId, noData, pricingData: { [shippingMethod]: { [bandId]: "fee" } },
  //           overrideKeys: ["<method>-<weightMin>-<weightMax>", ...] }
  //    Authoritative for the merged default+override view, but the controller projects the
  //    rows onto a FIXED 3-service x 21-band grid server-side (lines 520-561), so anything
  //    outside that grid is dropped before it reaches us.
  //
  // 2. GET /brands/:brandId/shipping-rules (shipping.controller.js:147), paginated
  //      -> { shippingRules: [{ id, shippingMethod, weightMin, weightMax, fee, weightRange,
  //             feeFormatted, isActive, region, carrierType, createdAt, updatedAt }],
  //           pagination: { page, limit, totalCount, totalPages, hasNextPage, ... },
  //           meta: { searchTerm, sortBy, sortDirection, totalShippingMethods } }
  //    Raw, unprojected rows - this is what makes a row on some other shippingMethod or some
  //    other weight band visible and editable. It filters on the literal :brandId, so it can
  //    only be used for a real brand; global defaults live on brandId NULL and no endpoint
  //    exposes them raw (see the note on the "default" branch below).
  const loadPricingData = useCallback(async () => {
    try {
      setLoadingData(true);
      setLoadError(null);
      setRecoveredRowCount(0);
      setRulesUnavailable(false);

      const brandIdParam = brand?.id || "default";
      const isDefault = brandIdParam === "default";
      setIsDefaultMode(isDefault);

      const [pricingResult, rulesResult] = await Promise.allSettled([
        apiClient.request(`/brands/${brandIdParam}/shipping-pricing`),
        // Global defaults (brandId NULL) are not reachable through /shipping-rules, which
        // matches on the literal path segment. Skip the call rather than fire one that can
        // only ever return an empty page.
        isDefault
          ? Promise.resolve({ shippingRules: [] })
          : apiClient.getAllShippingFeeRules(brandIdParam),
      ]);

      if (pricingResult.status === 'rejected') throw pricingResult.reason;

      const response = pricingResult.value;
      const ruleRows = rulesResult.status === 'fulfilled'
        ? (rulesResult.value?.shippingRules || [])
        : [];

      if (rulesResult.status === 'rejected' && !isDefault) {
        console.error('Error loading raw shipping rules:', rulesResult.reason);
        setRulesUnavailable(true);
      }

      if (response?.success && response.pricingData) {
        // Start from the API's merged grid, then fold in any raw row it dropped.
        const merged = JSON.parse(JSON.stringify(response.pricingData));
        const overrides = new Set(response.overrideKeys || []);
        let recovered = 0;

        ruleRows.forEach((row) => {
          const method = typeof row?.shippingMethod === 'string' ? row.shippingMethod.trim() : '';
          const min = Number(row?.weightMin);
          const max = Number(row?.weightMax);
          const fee = Number(row?.fee);
          if (!method || !Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(fee)) {
            return;
          }

          const bandId = bandIdFor(min, max);
          if (!merged[method]) merged[method] = {};
          if (merged[method][bandId] === undefined || merged[method][bandId] === '') {
            merged[method][bandId] = String(fee);
            recovered += 1;
          }
          // Every row from /shipping-rules is scoped to this brand, so it is by definition
          // a brand override and should be highlighted as one.
          overrides.add(`${method}-${min}-${max}`);
        });

        setPricingData(merged);
        // Store deep copy of original data for diff comparison
        setOriginalPricingData(JSON.parse(JSON.stringify(merged)));
        setRecoveredRowCount(recovered);

        // Service columns, in a stable order: whatever the pricing endpoint declares first,
        // then any additional method found on a raw row.
        const seenMethods = new Set();
        const methodNames = [];
        const addMethod = (name) => {
          if (typeof name !== 'string') return;
          const trimmed = name.trim();
          if (!trimmed) return;
          const key = trimmed.toLowerCase();
          if (seenMethods.has(key)) return;
          seenMethods.add(key);
          methodNames.push(trimmed);
        };
        Object.keys(merged).forEach(addMethod);
        ruleRows.forEach((row) => addMethod(row?.shippingMethod));

        const needsFallback = methodNames.length === 0;
        if (needsFallback) FALLBACK_SERVICE_NAMES.forEach(addMethod);
        setUsingFallbackServices(needsFallback);
        setServices(methodNames.map((name) => ({ id: name, name })));

        setWeightRanges(buildWeightBands(ruleRows));

        // Store override keys for highlighting brand-specific overrides
        setOverrideKeys(isDefault ? new Set() : overrides);
      }
    } catch (err) {
      console.error('Error loading pricing data:', err);
      setLoadError('Failed to load shipping pricing from the API.');
      // No database answer at all - show the fallback columns and say so.
      setServices(FALLBACK_SERVICE_NAMES.map((name) => ({ id: name, name })));
      setUsingFallbackServices(true);
      setWeightRanges(buildWeightBands([]));
      setOverrideKeys(new Set());
    } finally {
      setLoadingData(false);
    }
  }, [brand?.id]);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  // EditableCell component with proper event handling
  const EditableCell = ({
    serviceId,
    weightRangeId,
    weightMin,
    weightMax,
    value,
    disabled: cellDisabled,
    overrideKeysSet,
    isDefault
  }) => {
    const cellKey = `${serviceId}-${weightRangeId}`;
    const isEditing = editingCell?.serviceId === serviceId && editingCell?.weightRangeId === weightRangeId;
    const tempValue = tempValues[cellKey] || value || '';
    const error = errors[cellKey];

    // Check if this cell is a brand-specific override (not in default mode)
    const overrideKey = `${serviceId}-${weightMin}-${weightMax}`;
    const isOverride = !isDefault && overrideKeysSet.has(overrideKey);

    const handleCellClick = useCallback((e) => {
      e.stopPropagation(); // Prevent IndexTable row selection
      if (!cellDisabled && !loading) {
        setEditingCell({ serviceId, weightRangeId });
        setTempValues(prev => ({ ...prev, [cellKey]: value || '' }));
      }
    }, [serviceId, weightRangeId, value, cellDisabled, cellKey]);

    const handleValueChange = useCallback((newValue) => {
      setTempValues(prev => ({ ...prev, [cellKey]: newValue || "" }));

      // Clear error when user starts typing
      if (error) {
        setErrors(prev => ({ ...prev, [cellKey]: null }));
      }
    }, [cellKey, error]);

    const validateAndSave = useCallback(() => {
      const trimmedValue = tempValues[cellKey].trim();

      // Allow empty values - treat as removing pricing for this weight/service
      if (!trimmedValue) {
        setPricingData(prev => ({
          ...prev,
          [serviceId]: {
            ...prev[serviceId],
            [weightRangeId]: ''
          }
        }));
        setHasChanges(true);
        setEditingCell(null);
        setErrors(prev => ({ ...prev, [cellKey]: null }));
        return;
      }

      const price = parseFloat(trimmedValue);

      // Validation for non-empty values
      if (isNaN(price) || price < 0) {
        setErrors(prev => ({ ...prev, [cellKey]: 'Enter a valid price' }));
        return;
      }

      if (price > 999.99) {
        setErrors(prev => ({ ...prev, [cellKey]: 'Price must be less than $999.99' }));
        return;
      }

      // Save the value
      setPricingData(prev => ({
        ...prev,
        [serviceId]: {
          ...prev[serviceId],
          [weightRangeId]: trimmedValue
        }
      }));

      setHasChanges(true);
      setEditingCell(null);
      setErrors(prev => ({ ...prev, [cellKey]: null }));
    }, [tempValues[cellKey], serviceId, weightRangeId, cellKey]);

    const handleBlur = useCallback(() => {

      validateAndSave();
    }, [validateAndSave]);

    const handleKeyPress = useCallback((e) => {

      if (e.key === 'Enter') {
        validateAndSave();
      } else if (e.key === 'Escape') {
        // Cancel editing
        setEditingCell(null);
        setTempValues(prev => ({ ...prev, [cellKey]: value || '' }));
        setErrors(prev => ({ ...prev, [cellKey]: null }));
      }
    }, [validateAndSave, cellKey, value]);

    if (isEditing) {
      return (
        <Box onClick={handleCellClick} style={{ maxWidth: '120px' }}>
          <TextField
            value={tempValues[cellKey]}
            onChange={handleValueChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyPress}
            prefix="$"
            type="number"
            step="0.01"
            min="0"
            max="999.99"
            placeholder="0.00"
            align="right"
            autoFocus
            selectTextOnFocus
            error={error}
            disabled={cellDisabled || loading}
            autoComplete="off"
          />
        </Box>
      );
    }

    return (
      <Box
        onClick={handleCellClick}
        style={{
          cursor: cellDisabled ? 'default' : 'pointer',
          minHeight: '44px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: isOverride ? '#e3f2fd' : 'transparent',
          padding: isOverride ? '8px' : '0',
          borderRadius: isOverride ? '4px' : '0',
          margin: isOverride ? '-4px' : '0'
        }}
      >
        <Text
          variant="bodyMd"
          fontWeight="medium"
          tone={error ? 'critical' : value && parseFloat(value) > 0 ? 'base' : 'subdued'}
        >
          {value && parseFloat(value) > 0 ? formatCurrency(parseFloat(value)) : '—'}
        </Text>
        {error && (
          <Text variant="bodySm" tone="critical">
            {error}
          </Text>
        )}
      </Box>
    );
  };

  // Handle cancel - reload original data
  const handleCancel = useCallback(() => {
    loadPricingData(); // Reload from API
    setHasChanges(false);
    setEditingCell(null);
    setTempValues({});
    setErrors({});
  }, [loadPricingData]);

  // Resolve a weight band id back to its numeric bounds. Consumers write these straight into
  // PATCH /brands/:brandId/shipping-fees, so they must never be undefined - previously each
  // page looked the id up in its own hardcoded copy of the ladder, which produced
  // weightMin: undefined for any band outside it.
  const bandBoundsById = useMemo(() => {
    const map = new Map();
    weightRanges.forEach((band) => map.set(band.id, { min: band.min, max: band.max }));
    return map;
  }, [weightRanges]);

  const resolveBandBounds = useCallback((weightRangeId) => {
    const known = bandBoundsById.get(weightRangeId);
    if (known) return known;
    // Fall back to parsing the id itself ("12-14" -> 12, 14) so a cell can still be saved.
    const parts = String(weightRangeId).split('-');
    if (parts.length !== 2) return null;
    const min = Number(parts[0]);
    const max = Number(parts[1]);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    return { min, max };
  }, [bandBoundsById]);

  // Handle bulk save - computes diff and sends only changed cells
  const handleSave = useCallback(async () => {
    if (onSave && hasChanges) {
      // Compute only changed cells
      const changes = [];
      const pushChange = (serviceId, weightRangeId, oldValue, newValue) => {
        const bounds = resolveBandBounds(weightRangeId);
        if (!bounds) {
          console.error(`Skipping shipping fee change with unresolvable weight band "${weightRangeId}"`);
          return;
        }
        changes.push({
          serviceId,
          weightRangeId,
          weightMin: bounds.min,
          weightMax: bounds.max,
          oldValue,
          newValue,
        });
      };

      Object.entries(pricingData).forEach(([serviceId, weightPrices]) => {
        Object.entries(weightPrices || {}).forEach(([weightRangeId, price]) => {
          const originalPrice = originalPricingData[serviceId]?.[weightRangeId] || '';
          if (price !== originalPrice) {
            pushChange(serviceId, weightRangeId, originalPrice, price);
          }
        });
      });

      // Also check for deletions (keys in original but not in current or empty in current)
      Object.entries(originalPricingData).forEach(([serviceId, weightPrices]) => {
        Object.entries(weightPrices || {}).forEach(([weightRangeId, originalPrice]) => {
          const currentPrice = pricingData[serviceId]?.[weightRangeId] || '';
          // If original had a value but current doesn't, and we haven't already tracked this change
          if (originalPrice && !currentPrice) {
            const alreadyTracked = changes.some(
              c => c.serviceId === serviceId && c.weightRangeId === weightRangeId
            );
            if (!alreadyTracked) {
              pushChange(serviceId, weightRangeId, originalPrice, '');
            }
          }
        });
      });

      try {
        await onSave(changes, pricingData);
        // Reload from API to get updated overrideKeys and fresh data
        await loadPricingData();
        setHasChanges(false);
      } catch (err) {
        console.error('Failed to save pricing data:', err);
      }
    }
  }, [onSave, pricingData, originalPricingData, hasChanges, loadPricingData, resolveBandBounds]);

  // Handle reset to default - delete all brand overrides
  const handleResetToDefault = useCallback(async () => {
    if (!brand?.id || isDefaultMode) return;

    try {
      setResetting(true);
      const response = await apiClient.resetShippingFeesToDefault(brand.id);

      if (response.success) {
        setShowResetModal(false);
        // Reload data to show global defaults
        await loadPricingData();
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to reset shipping fees:', error);
      setLoadError('Failed to reset shipping fees to default');
    } finally {
      setResetting(false);
    }
  }, [brand?.id, isDefaultMode, loadPricingData]);

  // IndexTable configuration
  const resourceName = {
    singular: 'weight range',
    plural: 'weight ranges',
  };

  // Column headings follow the API-derived service list rather than asserting three BoxC names.
  // Headings are dynamic — one column per service — so the tips are built here rather than
  // in a static column array. See utils/table.jsx for why an empty table needs a synthetic
  // row to keep them.
  const tableHeadings = useMemo(() => [
    {
      title: 'Weights/Service',
      alignment: 'start',
      tooltipContent: 'The weight band a shipment falls into. A shipment is priced by the first band its weight fits.',
      tooltipWidth: 'wide',
    },
    ...services.map((service) => ({
      title: service.name,
      alignment: 'start',
      tooltipContent: `What 2W charges for a shipment in each weight band on ${service.name}. This is the price billed to the brand, not the carrier's own cost — no carrier cost feed exists in this system.`,
      tooltipWidth: 'wide',
    })),
  ], [services]);

  if (loading || loadingData) {
    return (
      <Box padding="400">
        <InlineStack align="center" gap="200">
          <Spinner size="large" />
          <Text>Loading shipping pricing table...</Text>
        </InlineStack>
      </Box>
    );
  }

  return (
    <BlockStack gap="300">
      {/* Error banner for data loading issues */}
      {loadError && (
        <Banner tone="critical" onDismiss={() => setLoadError(null)}>
          <Text>{loadError}</Text>
        </Banner>
      )}

      {/* Say so when the columns are not database-backed, rather than implying they are */}
      {usingFallbackServices && (
        <Banner tone="warning">
          <Text>
            No shipping services were returned for {isDefaultMode ? 'the global defaults' : 'this brand'},
            so the columns below are a built-in placeholder list, not configured data.
            Anything you enter is still saved to the database.
          </Text>
        </Banner>
      )}

      {/* Rows the fixed /shipping-pricing grid dropped, recovered from the raw rules */}
      {recoveredRowCount > 0 && (
        <Banner tone="info">
          <Text>
            {recoveredRowCount} saved shipping fee{recoveredRowCount !== 1 ? 's' : ''} fall outside
            the standard service/weight grid and {recoveredRowCount !== 1 ? 'were' : 'was'} loaded
            from this brand&apos;s own rules.
          </Text>
        </Banner>
      )}

      {/* Be explicit when we could not confirm the raw rows */}
      {rulesUnavailable && (
        <Banner tone="warning">
          <Text>
            This brand&apos;s raw shipping rules could not be loaded, so any fee on a service or
            weight band outside the standard grid may not be shown. Reload to try again.
          </Text>
        </Banner>
      )}

      {/* Reset to Default button - only show for brand mode with overrides */}
      {!isDefaultMode && overrideKeys.size > 0 && (
        <InlineStack align="end">
          <Button
            variant="plain"
            tone="critical"
            onClick={() => setShowResetModal(true)}
          >
            Reset to Default ({overrideKeys.size} override{overrideKeys.size !== 1 ? 's' : ''})
          </Button>
        </InlineStack>
      )}

      {/* Reset confirmation modal */}
      <Modal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Shipping Fees to Default"
        primaryAction={{
          content: 'Reset to Default',
          destructive: true,
          loading: resetting,
          onAction: handleResetToDefault,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowResetModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text>
              This will remove all {overrideKeys.size} custom shipping fee override{overrideKeys.size !== 1 ? 's' : ''} for this brand.
            </Text>
            <Text>
              After reset, this brand will use the global default shipping fees.
            </Text>
            <Text fontWeight="semibold" tone="caution">
              This action cannot be undone.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Header with save/cancel buttons */}
      {hasChanges && (
        <Banner tone="info">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="bodyMd" fontWeight="medium">Unsaved changes</Text>
            <InlineStack gap="400">
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave} loading={loading}>
                Save Changes
              </Button>
            </InlineStack>
          </InlineStack>
        </Banner>
      )}
      {/* Instructions */}
      <Box padding="200" background="bg-surface-info-subdued" borderRadius="200">
        <BlockStack gap="100">
          <Text variant="bodyMd" fontWeight="semibold">
            How to use this pricing table:
          </Text>
          <Text variant="bodySm">
            • Click on any price cell to edit the shipping fee for that weight range and service
          </Text>
          <Text variant="bodySm">
            • Press Enter to save or Escape to cancel editing. Leave empty to remove pricing.
          </Text>
          <Text variant="bodySm">
            • Changes are highlighted and require saving before taking effect
          </Text>
          {!isDefaultMode && (
            <Text variant="bodySm">
              • Cells with <span style={{ backgroundColor: '#e3f2fd', padding: '2px 6px', borderRadius: '4px' }}>blue background</span> are custom overrides for this brand. Other values use global defaults.
            </Text>
          )}
        </BlockStack>
      </Box>
      {/* Pricing Table, capped at five rows.
          
          There are 23 weight bands seeded and any saved rule adds more, so at full height the
          table pushed Default Return Fee and Marketplace Overrides far below the fold — on this
          tab you scroll past a price grid to reach an unrelated setting. The container scrolls
          instead, and the column header is pinned (see .tw-scroll-table in style.css) because a
          price grid whose "BoxC Parcel / Plus / Priority" headings have scrolled away is a grid
          you cannot safely type into. */}
      <Box borderColor="border" borderWidth="025" borderRadius="200">
        <div className="tw-scroll-table">
        <IndexTable
          resourceName={resourceName}
          itemCount={weightRanges.length || 1}
          selectedItemsCount={0}
          onSelectionChange={() => { }} // No selection needed
          headings={tableHeadings}
          selectable={false}
          hasMoreItems={false}
          condensed={!smUp}
        >
          {weightRanges.length === 0 ? (
            <IndexTable.Row id="empty-weights" position={0}>
              <IndexTable.Cell colSpan={tableHeadings.length}>
                <Box padding="500">
                  <Text alignment="center" tone="subdued" variant="bodySm">
                    No weight band configured yet. Each row will be one band, priced across
                    every service in the columns above.
                  </Text>
                </Box>
              </IndexTable.Cell>
            </IndexTable.Row>
          ) : weightRanges.map((weightRange, index) => (
            <IndexTable.Row
              id={weightRange.id}
              key={weightRange.id}
              position={index}
            >
              {/* Weight Range Column */}
              <IndexTable.Cell>
                <BlockStack gap="100">
                  <Text variant="bodyMd" fontWeight="semibold">
                    {weightRange.range}
                  </Text>
                  <Text variant="bodySm" tone="subdued">
                    {weightRange.kg}
                  </Text>
                </BlockStack>
              </IndexTable.Cell>

              {/* Service Price Columns */}
              {services.map((service) => (
                <IndexTable.Cell key={service.id}>
                  <EditableCell
                    serviceId={service.id}
                    weightRangeId={weightRange.id}
                    weightMin={weightRange.min}
                    weightMax={weightRange.max}
                    value={pricingData[service.id]?.[weightRange.id]}
                    disabled={disabled}
                    overrideKeysSet={overrideKeys}
                    isDefault={isDefaultMode}
                  />
                </IndexTable.Cell>
              ))}
            </IndexTable.Row>
          ))}
        </IndexTable>
        </div>
      </Box>
    </BlockStack>
  );
};

export default ShippingPricingTable;